import { SORT_DIRECTIONS, SORT_TYPES } from "./TableConstants";
import {
  compareAlphabetical, compareDate, compareLength, compareNumerical, compareTime
} from "../../../utils/SortingUtils";

/**
 * Sorts table data based on the provided sort key, type and direction.
 * @param {Array} data the data to be sorted
 * @param {String} sortKey the key to sort by, i.e. 'lastName'
 * @param {String} sortType the type of sorting logic to use, i.e. SORT_TYPES.ALPHABETICAL
 * @param {String} sortDirection the direction of sorting, i.e. SORT_DIRECTIONS.ASCENDING
 * @returns {Array} the sorted data
 */
const sortTableData = (data, sortKey, sortType, sortDirection) => {
  try {
    if (Array.isArray(data)) {
      if (sortKey && sortType && sortDirection) {
        switch (sortType) {
          case SORT_TYPES.ALPHABETICAL:
            return sortDirection === SORT_DIRECTIONS.DESCENDING
              ? data.toSorted((a, b) => compareAlphabetical(a[sortKey], b[sortKey]) * -1)
              : data.toSorted((a, b) => compareAlphabetical(a[sortKey], b[sortKey]));
          case SORT_TYPES.NUMERICAL:
            return sortDirection === SORT_DIRECTIONS.DESCENDING
              ? data.toSorted((a, b) => compareNumerical(a[sortKey], b[sortKey]) * -1)
              : data.toSorted((a, b) => compareNumerical(a[sortKey], b[sortKey]));
          case SORT_TYPES.DATE:
            return sortDirection === SORT_DIRECTIONS.DESCENDING
              ? data.toSorted((a, b) => compareDate(a[sortKey], b[sortKey]) * -1)
              : data.toSorted((a, b) => compareDate(a[sortKey], b[sortKey]));
          case SORT_TYPES.TIME:
            return sortDirection === SORT_DIRECTIONS.DESCENDING
              ? data.toSorted((a, b) => compareTime(a[sortKey], b[sortKey]) * -1)
              : data.toSorted((a, b) => compareTime(a[sortKey], b[sortKey]));
          case SORT_TYPES.LENGTH:
            return sortDirection === SORT_DIRECTIONS.DESCENDING
              ? data.toSorted((a, b) => compareLength(a[sortKey], b[sortKey]) * -1)
              : data.toSorted((a, b) => compareLength(a[sortKey], b[sortKey]));
          default:
            return [...data];
        }
      } else {
        return [...data];
      }
    }

    return [];
  } catch (e) {
    return [];
  }
};

/**
 * Formats multiple values for display within a table cell.
 * @param {Array} values the values to be formatted, can be an array of strings or objects
 * @param {String} valueKey optional key to be used when formatting an array of objects, i.e. 'lastName'
 * @param {String} divider the divider to be used between values, defaults to ', '
 * @param {String} endDivider the divider to be used before the last value, defaults to ' & '
 * @returns {String} the formatted string of values
 */
const formatMultipleValuesForDisplay = (values, valueKey = undefined, divider = ', ', endDivider = ' & ') => {
  try {
    let formattedValues = '';

    if (Array.isArray(values) && values.length > 0) {
      for (let i = 0; i < values.length; i++) {
        const value = valueKey ? values[i][valueKey] : values[i];
        if (value) {
          if (formattedValues) {
            if (i === values.length - 1) {
              formattedValues += `${endDivider}${value}`;
            } else {
              formattedValues += `${divider}${value}`;
            }
          } else {
            formattedValues = value;
          }
        }
      }
    }

    return formattedValues;
  } catch (e) {
    return '';
  }
};

export { sortTableData, formatMultipleValuesForDisplay };